// Achievements, and everything they hand over.
//
// The whole system hangs off one plain bag of counters: an achievement is just a
// predicate over it, so "has this unlocked?" is a pure question with no hidden
// state, and saving the player's progress is a single JSON blob.
//
// Nothing here reads the game directly. flow.js and background.js call record()
// when something happens; that is the only way in.
//
// It does read the mode and the screen, though: CLASSIC counts towards nothing, and
// neither does the front end.

import { mods, DEFAULT_MODE } from './modes.js';
import { state } from './state.js';
import { ACCESSORIES, ACCESSORY_SLOTS, CPU_LEVEL_DEFAULT, MAX_RESULTS, SPECIES, TARGET_WINS_DEFAULT, TURN_SECONDS_DEFAULT } from './config.js';
import { t } from './i18n.js';

const STORAGE_KEY = 'qbasicgorillas/progress/v1';
/**
 * Where this game saved before it was called QBasic Gorillas. Read once, and
 * only dropped after a successful write under the new name — renaming the game
 * must not cost anyone their achievements.
 */
const LEGACY_KEY = 'gorillas-neon-siege/progress/v1';

/** Everything the achievements are judged on. */
const EMPTY_STATS = {
  throws: 0,
  hits: 0,
  selfHits: 0,
  moonHits: 0,
  roundsWon: 0,
  matchesWon: 0,
  cpuMatchesWon: 0,
  flawlessMatches: 0,
  // Matches won with nothing spent in them: no ult, no defence, no crate. The flag that says so
  // is per match and lives in the state (see `spentInMatch`), because it is a fact about the
  // match; this is only the tally of the ones that came out that way.
  bareMatches: 0,
  /** Matches won over a wire, one way or the other. */
  roomWins: 0,
  codeWins: 0,
  bestHitStreak: 0,
  /** Crates broken open, and defences put up. Both are new things to be good at. */
  crates: 0,
  guards: 0,
  /** Ids of the passing sky things caught: meteor, iss, rocket, banner, airforce, batsignal. */
  skySeen: [],
  /** Ids of the specials actually spent, so "use six different ones" can be asked. */
  specialsSeen: [],
  /** Bumeranes que han vuelto a la mano. El logro del tiro que regresa. */
  boomerangs: 0,
};

/** Achievements in the order they are shown. */
const ACHIEVEMENTS = [
  { id: 'first-blood', when: s => s.hits >= 1 },
  { id: 'round-won', when: s => s.roundsWon >= 1 },
  { id: 'butterfingers', when: s => s.selfHits >= 1 },
  { id: 'sharpshooter', when: s => s.bestHitStreak >= 3 },
  { id: 'match-won', when: s => s.matchesWon >= 1 },
  { id: 'cpu-slayer', when: s => s.cpuMatchesWon >= 1 },
  { id: 'moon-shot', when: s => s.moonHits >= 1 },
  { id: 'sky-watcher', when: s => s.skySeen.length >= 3 },
  { id: 'flawless', when: s => s.flawlessMatches >= 1 },
  { id: 'veteran', when: s => s.matchesWon >= 3 },
  { id: 'thrower', when: s => s.throws >= 50 },
  { id: 'rounds-10', when: s => s.roundsWon >= 10 },
  { id: 'sharpshooter-plus', when: s => s.bestHitStreak >= 6 },
  { id: 'moon-3', when: s => s.moonHits >= 3 },
  { id: 'cpu-3', when: s => s.cpuMatchesWon >= 3 },
  { id: 'flawless-3', when: s => s.flawlessMatches >= 3 },
  { id: 'veteran-2', when: s => s.matchesWon >= 5 },
  // --- what the newer things are for -------------------------------------------
  // A crate is a reward you have to go and get, a defence is a turn spent on not being hit, and
  // a room is a match somebody had to turn up for. None of them had an achievement of its own,
  // so the only players who ever saw them were the ones who happened to use them.
  { id: 'scavenger', when: s => s.crates >= 5 },
  { id: 'wall', when: s => s.guards >= 5 },
  { id: 'arsenal', when: s => s.specialsSeen.length >= 6 },
  { id: 'bare-handed', when: s => s.bareMatches >= 1 },
  { id: 'room-won', when: s => s.roomWins >= 1 },
  { id: 'room-3', when: s => s.roomWins >= 3 },
  { id: 'by-mail', when: s => s.codeWins >= 1 },
  // El bumerán tiene el suyo porque su gracia no es acertar: es recuperarlo. Tres veces ya
  // significa que alguien ha entendido que ese tiro se juega distinto.
  { id: 'boomerang-back', when: s => s.boomerangs >= 3 },
];

/** The antics every gorilla already knows. The rest have to be earned. */
export const STARTING_ANTICS = ['scratch', 'head', 'chest'];

/**
 * What each achievement hands over. A gesture joins the pool of things the
 * gorillas do on their own; a special becomes a button the player can spend.
 */
const REWARDS = {
  // First blood hands over a defence, and on purpose: it is the first achievement anybody
  // earns, and this is the one defence that teaches the shape of a turn — a shot can be
  // survived by having decided before it was thrown. It also had nothing but a gesture in
  // it, which is a poor trophy for the first thing a new player ever does.
  //
  // La seta, y antes el campo del superguerrero: la seta espera igual que él esperaba —se gasta
  // con el platanazo del que te salva, y con nada más—, así que es la que enseña lo mismo.
  'first-blood': { antic: 'wave', specials: ['mushroom'] },
  'round-won': { antic: 'pick', specials: ['ball'] },
  butterfingers: { antic: 'shrug' },
  'match-won': { antic: 'stretch', specials: ['mushroom'] },
  'cpu-slayer': { antic: 'shake', specials: ['spring'] },
  sharpshooter: { special: 'flare' },
  'sky-watcher': { special: 'heavy' },
  flawless: { special: 'fat' },
  'moon-shot': { special: 'lunar' },
  // The two killstreaks, hung off the two achievements that were still handing
  // over nothing. Fifty bananas is a fair trade for something with a fuse, and a
  // killstreak is what a veteran has coming to them.
  thrower: { special: 'grenade' },
  veteran: { special: 'drone' },
  // The leap goes to whoever has already proved they can aim. Six hits in a row
  // is not the achievement of somebody who needs the help, which is exactly why
  // the shot that mostly aims itself is theirs. The crown that already hung off
  // this one is added below without being lost; that is what the merge is for.
  'sharpshooter-plus': { special: 'leap' },
  // The two new gestures go to the two new things you do with your hands: the crate you went and
  // broke open, and the turn you spent standing behind something instead of throwing.
  //
  // Y la caja regala el bumerán, que es el tiro que se recupera: sale de donde sale un premio y
  // con la forma de un premio — algo que se usa y no se pierde. Es, con diferencia, el especial
  // que mejor le sienta a quien acaba de aprender que las cajas se rompen a propósito.
  scavenger: { antic: 'clap', special: 'boomerang' },
  wall: { antic: 'flex' },
  // Y recuperarlo tres veces da una carga más del mismo tiro, que es lo único que se puede
  // regalar a quien ya lo tiene sin inventarle otro premio.
  'boomerang-back': { specials: ['boomerang'] },
};

// Accessories are no longer rewards, so nothing is merged in from them here. They used to
// be: an accessory carried its own unlock condition and the reward was read off it, with a
// merge rather than an assignment because more than one thing can hang off the same
// achievement — a plain assignment is how the beanie ate the grenade and the eye patch ate
// the shrug. The wardrobe is free now, so the merge went with it.

/**
 * The specials, in the order the player cycles through them. Each one bends one
 * number the physics already uses, and is spent on the shot it is fired with.
 */export const SPECIALS = {
  flare: {
    uses: 2,
    aimScale: 2,
  },
  heavy: {
    uses: 2,
    windScale: 0.12,
  },
  fat: {
    uses: 2,
    craterScale: 1.9,
  },
  lunar: {
    uses: 1,
    windScale: 0,
    craterScale: 2.4,
  },
  grenade: {
    uses: 2,
    grenade: true,
  },
  sheep: {
    uses: 1,
    sheep: true,
  },
  drone: {
    uses: 1,
    drone: true,
    // The paint is worth nothing if you cannot see where the shot is going, so
    // the preview reaches further while it is up, same as Rastro.
    //
    // Y de un solo uso desde que es un arma: suelta una bomba sobre el que tiene pintado y le
    // acaba la ronda, así que es un ult como `leap` y se gasta como él.
    aimScale: 1.6,
  },
  leap: {
    // One, and one only. It is the strongest thing in the game and it has to be
    // spent like it.
    uses: 1,
    leap: true,
    windScale: 0,
  },
  shield: {
    uses: 1,
    // A defence, not an attack. `guard` is what puts it in the protections list
    // rather than the ults list, and it is written here instead of being inferred
    // from `shield`/`spring`/`absorb` so that the two lists cannot drift apart
    // from the physics.
    guard: true,
    shield: true,
  },
  spring: {
    // Like the shield, one shot — hit or miss — because it is a thing standing on a
    // roof, and things on roofs do not wait.
    uses: 1,
    guard: true,
    spring: true,
  },
  mushroom: {
    // Unlike the shield, it waits. It is spent only by the shot it saves you from,
    // which is what a mushroom does.
    //
    // Aquí vivía un `absorb: true` que no leía nadie: el estado de la seta es `state.mushroom`,
    // una casilla propia, y el barrido del proyectil y las explosiones preguntan por ella. Un
    // campo que no lee nadie no documenta nada: dice que la seta absorbe y no es él quien lo
    // sostiene, así que cualquiera que lo tocara creería haber cambiado algo.
    uses: 1,
    guard: true,
  },
  // El superguerrero vivió aquí: `uses: 1`, `guard: true` y `saiyan: true`, un campo que
  // aguantaba un platanazo. Se quitó porque el turno que lo activaba no volvía a su sitio. El
  // dibujo sigue en render/gorilla.js, inerte, porque no lee nada que ya no exista; si algún
  // día vuelve, vuelve poniendo esto otra vez y devolviéndole el regalo del logro.
  ball: {
    // Not a posture and not a defence: it is an ordinary throw of an unordinary
    // thing, so it costs nothing but the charge.
    uses: 1,
    ball: true,
  },
  boomerang: {
    // El único especial que se recupera: sale, se abre y vuelve a la mano, y cogerlo devuelve la
    // carga y deja el turno donde estaba. Una sola, porque un tiro que no se gasta es un tiro que
    // se puede repetir toda la partida, y lo que lo equilibra no es el número de cargas sino el
    // reloj: la vuelta tarda más de un segundo y ese tiempo se va del turno.
    uses: 1,
    boomerang: true,
  },
};

/**
 * What every side starts a match with, whether it has earned anything or not.
 *
 * One ult and one protection, and deliberately the SAME for both sides. Before
 * this, a new player had nothing at all: specials only arrived as achievements
 * fell, so the first match anyone ever played had no specials in it, and the
 * promise of the system was invisible until somebody had already won a few
 * games. A starter pair fixes that.
 *
 * The same pair for both sides is the answer to the obvious worry about a
 * hand-out, which is that it hands out an advantage. It cannot: the player and
 * the CPU open the match holding the same two things. Randomising it would make
 * some matches start luckier than others; this way the only difference between
 * two players is what they have earned.
 *
 * RASTRO and ESCUDO are chosen to teach rather than to win. One shows you where
 * the shot is going, which is the thing a new player most needs; the other
 * blocks a single shot, which is the thing that makes a turn-based game feel
 * like it has tactics in it. Neither can end a round on its own.
 */
// The sheep has joined them rather than being earned, and on purpose. It is the oldest joke
// in the genre and it is a toy, not a trophy: whoever plays their first match should be able
// to throw one at somebody. `uses: 1` is what keeps it a moment rather than a strategy, and
// the machine is dealt the same pair, so the opening of a match stays symmetric.
export const STARTER_SPECIALS = ['flare', 'shield', 'sheep'];

/** True for the pair everybody is given. */
export function isStarterSpecial(id) {
  return STARTER_SPECIALS.includes(id);
}

/**
 * The specials of one kind, in SPECIALS order. 'guard' is the protections, and
 * anything that is not a guard is an ult — the default, so a new special only
 * has to say so if it is a defence.
 */
export function specialsOfKind(kind) {
  return Object.keys(SPECIALS).filter(id => (kind === 'guard'
    ? SPECIALS[id].guard === true
    : SPECIALS[id].guard !== true));
}

/**
 * Which achievement hands a special over, or null if it is one of the starters.
 * The equipment list has to be able to tell a player how to get a greyed-out
 * entry, and that answer already exists here; writing it out again in the UI is
 * how the two would come to disagree.
 */
export function specialSource(id) {
  const found = Object.keys(REWARDS).find(key => {
    const reward = REWARDS[key];
    if (!reward) return false;
    return reward.special === id || (reward.specials || []).includes(id);
  });

  return found || null;
}

let stats = { ...EMPTY_STATS };
let unlocked = []; // achievement ids, in the order they were earned
/**
 * What each side is: { species, worn }, where species indexes SPECIES and worn
 * maps a slot to an accessory id. Two sides, always.
 */
let looks = [
  { species: 0, worn: {} },
  { species: 1, worn: {} },
];
/** The name this player sends to the shared board. Empty means "do not post". */
let name = '';
/** Finished matches, oldest first. Trimmed to MAX_RESULTS whenever it grows. */
let results = [];
/** The mode the player last chose, so a reload does not reset it to MODERN. */
let mode = DEFAULT_MODE;
/**
 * Seconds a player has to throw, or 0 for no clock. A match rule rather than a
 * piece of progress, but it lives with the mode because the two are asked for in
 * the same place and both have to survive a reload.
 */
let turnSeconds = TURN_SECONDS_DEFAULT;
/** How many rounds a match is, or 0 for one that does not end. Like the clock, a rule of the match
 * rather than a piece of progress, and it lives here for the same reason: it is asked for in the
 * same place and it has to survive a reload. */
let targetWins = TARGET_WINS_DEFAULT;

/** How long a turn lasts. 0 means nobody is being timed. */
export function savedTurnSeconds() {
  return turnSeconds;
}

/** Sets it, and remembers it. */
export function setTurnSeconds(seconds) {
  turnSeconds = seconds;
  save();
}

/**
 * How many rounds a match is won by, or 0 for a match with no end to it.
 *
 * The choice is the setup's and it is remembered, but the match is what plays by it: a match carries
 * its own `targetWins` (state.js), so changing the setting while a match is on does not move the
 * finish line under the two who are playing it.
 */
export function savedTargetWins() {
  return targetWins;
}

/** Sets it, and remembers it. */
export function setTargetWins(wins) {
  targetWins = wins;
  save();
}

/** Which CPU_LEVELS entry the machine plays at. */
let cpuLevel = CPU_LEVEL_DEFAULT;

/** The CPU's difficulty, asked for in the setup screen. */
export function savedCpuLevel() {
  return cpuLevel;
}

/** Sets it, and remembers it. */
export function setCpuLevel(id) {
  cpuLevel = id;
  save();
}

/**
 * Si esta máquina ya ha dicho dónde vive el equipamiento.
 *
 * Se guarda por lo mismo que los demás ajustes: el aviso de la primera partida sirve una vez, y
 * un aviso que sale en todas deja de ser un aviso y pasa a ser ruido que tapa el campo.
 */
let intro = false;

/** True mientras la primera partida no haya presentado el zurrón. */
export function introSeen() {
  return intro;
}

/** Lo apunta, y lo guarda con lo demás. */
export function markIntroSeen() {
  intro = true;
  save();
}
/** True when what got loaded came from the pre-rename key; see initProgress(). */
let loadedFromLegacy = false;
const listeners = [];

// --- persistence -----------------------------------------------------------

function load() {
  try {
    // Either key will do: the new one wins, and the old one is only there for
    // players who were here before the rename.
    let raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      raw = localStorage.getItem(LEGACY_KEY);
      loadedFromLegacy = !!raw;
    }
    if (!raw) return;
    const saved = JSON.parse(raw);
    // Merge rather than replace: a save from an older build is missing whatever
    // counters were added since, and those should start at zero, not undefined.
    stats = { ...EMPTY_STATS, ...(saved.stats || {}) };
    unlocked = Array.isArray(saved.unlocked) ? saved.unlocked : [];
    if (Array.isArray(saved.looks) && saved.looks.length === 2) {
      // Merged field by field: a save from the build that only had palettes has
      // no species, and one from before accessories has no worn map.
      looks = saved.looks.map((l, i) => ({
        species: Number.isInteger(l && l.species) ? l.species : i,
        worn: (l && l.worn) || {},
      }));
    }
    if (Array.isArray(saved.results)) results = saved.results;
    if (typeof saved.mode === 'string') mode = saved.mode;
    if (typeof saved.turnSeconds === 'number') turnSeconds = saved.turnSeconds;
    if (typeof saved.targetWins === 'number') targetWins = saved.targetWins;
    if (typeof saved.name === 'string') name = saved.name;
    // La dificultad, que se guardaba en memoria y no en el disco: `setCpuLevel` llama a `save()`
    // como los demás ajustes, pero este no estaba en la lista, así que al recargar volvía a
    // `normal` mientras el modo, el reloj y las rondas sí sobrevivían.
    if (typeof saved.cpuLevel === 'string') cpuLevel = saved.cpuLevel;
    // El onboarding, que también sobrevive a recargar: el aviso es de la primera partida de la
    // vida del perfil, no de la primera de cada sesión.
    if (saved.intro) intro = true;
  } catch {
    // A corrupt or unavailable store just means starting fresh; never fatal.
    stats = { ...EMPTY_STATS };
    unlocked = [];
    looks = [{ species: 0, worn: {} }, { species: 1, worn: {} }];
    results = [];
    mode = DEFAULT_MODE;
    name = '';
  }
}

function save() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ stats, unlocked, looks, results, mode, name, turnSeconds, targetWins, cpuLevel, intro }));
    // The new key now holds everything, so the old one is just clutter. Left in
    // place if the line above throws, so a failed write never loses anything.
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    // Private mode, quota, whatever. Losing progress beats breaking the game.
  }
}

// --- the public surface ----------------------------------------------------

/** Loads saved progress. Call once, before the game reads anything. */
export function initProgress() {
  load();
  // state.looks is what the renderer actually reads, so it has to be handed the
  // saved pair here. Without this the picker and the characters disagree: the
  // panel shows the new species while the sprite keeps the old one.
  state.looks = looks.map(l => ({ ...l, worn: { ...l.worn } }));

  // Renaming the game must not cost anyone their achievements. If the save came
  // from the old key, write it straight back under the new name, so the rename
  // finishes on the first load rather than whenever they next earn something.
  if (loadedFromLegacy) save();
}

export function onUnlock(fn) {
  listeners.push(fn);
}

/** Everything countably earned so far, for the panel and for tests. */
export function progressStats() {
  return { ...stats, bestHitStreak: stats.bestHitStreak, skySeen: [...stats.skySeen] };
}

/** The achievement list, each entry flagged with whether it is earned. */
export function achievementList() {
  return ACHIEVEMENTS.map(def => ({
    id: def.id,
    // Resolved on every call rather than stored: the panel has to be able to
    // redraw in a new language without the progress data knowing about any of it.
    name: t(`ach.${def.id}.name`),
    hint: t(`ach.${def.id}.hint`),
    unlocked: unlocked.includes(def.id),
    reward: REWARDS[def.id] || null,
  }));
}

export function unlockedCount() {
  // Counted against the list rather than from the saved ids: an achievement that is retired
  // leaves its id behind in the save of everyone who earned it, and a count that trusted the
  // save would say "18 of 17" for ever. The save keeps them and the list decides.
  return unlocked.filter(id => ACHIEVEMENTS.some(a => a.id === id)).length;
}

/** Antic names the gorillas may perform right now. */
export function unlockedAntics() {
  const names = new Set(STARTING_ANTICS);
  unlocked.forEach(id => {
    const reward = REWARDS[id];
    if (reward && reward.antic) names.add(reward.antic);
  });
  return names;
}

/** Special ids the player can carry, in SPECIALS order. */
export function unlockedSpecials() {
  // The starter pair is in there from the first frame, earned or not. Everything
  // else still has to be won, which is what keeps the awards worth chasing.
  const earned = new Set(STARTER_SPECIALS);
  unlocked.forEach(id => {
    const reward = REWARDS[id];
    if (!reward) return;
    // One, or several. Two achievements that between them handed over a gesture and
    // nothing else now hand over a special as well, without moving anything that
    // was already written here.
    if (reward.special) earned.add(reward.special);
    (reward.specials || []).forEach(special => earned.add(special));
  });
  return Object.keys(SPECIALS).filter(id => earned.has(id));
}

/** The unlocked specials of one kind, for the two lists in the equipment menu. */
export function unlockedSpecialsOfKind(kind) {
  const owned = new Set(unlockedSpecials());
  return specialsOfKind(kind).filter(id => owned.has(id));
}

/** A fresh set of uses, for the start of a match. */
export function resetSpecialUses() {
  return Object.fromEntries(unlockedSpecials().map(id => [id, SPECIALS[id].uses]));
}

/**
 * The kit the CPU is dealt for a match.
 *
 * It is dealt rather than earned, so it cannot come from achievements — the CPU
 * has none. Three rules keep it from being either a pushover or a cheat:
 *
 *  - it always gets the same starter pair the player gets, so the opening of
 *    every match is symmetric;
 *  - it gets as many EXTRAS as the player has earned extras, because a player
 *    holding eight specials against a CPU holding two is not a match, and the
 *    reverse is worse;
 *  - those extras are drawn at random from everything the game has, so it is
 *    never the same kit twice and the player cannot plan around it.
 */
export function dealCpuKit() {
  const kit = {};
  STARTER_SPECIALS.forEach(id => { kit[id] = SPECIALS[id].uses; });

  const extras = unlockedSpecials().filter(id => !isStarterSpecial(id)).length;
  // Copied before drawing: this array is thrown away as it is used so the same
  // special can never be dealt twice into one kit.
  const pool = Object.keys(SPECIALS).filter(id => !isStarterSpecial(id));

  for (let i = 0; i < extras && pool.length; i += 1) {
    const at = Math.floor(Math.random() * pool.length);
    const id = pool.splice(at, 1)[0];
    kit[id] = SPECIALS[id].uses;
  }

  return kit;
}

/** Wipes everything, for the "start over" button. */
export function clearProgress() {
  stats = { ...EMPTY_STATS };
  unlocked = [];
  results = [];
  save();
}

// --- looks: species and accessories ---------------------------------------

/** What each side is, as { species, worn }. */
export function savedLooks() {
  return state.looks.map(l => ({ species: l.species, worn: { ...l.worn } }));
}

/** Picks a species, or changes what a side is wearing. Saved immediately. */
export function setLook(side, patch) {
  looks[side] = { ...looks[side], ...patch };
  state.looks[side] = { species: looks[side].species, worn: { ...looks[side].worn } };
  save();
}

/** Puts an accessory on, or takes it off if it was already on. */
export function toggleAccessory(side, slot, id) {
  const worn = { ...(state.looks[side].worn || {}) };

  if (worn[slot] === id) delete worn[slot];
  else worn[slot] = id;

  setLook(side, { worn });
}

/**
 * This side's character, as it goes over a wire.
 *
 * It has to travel. Both machines draw both figures and the picture is the hitbox — a species
 * is a target of a different size — so a rival drawn out of this machine's own save file is a
 * target of the wrong size, and the two of them are playing slightly different games.
 */
export function myLook() {
  const side = state.online && state.seat ? 1 : 0;
  const look = state.looks[side] || { species: side, worn: {} };

  return { species: look.species, worn: { ...look.worn } };
}

/**
 * Puts the rival's character on their side of the screen, in memory only.
 *
 * Deliberately not `setLook`: that one writes the save file, and a rival's wardrobe is not
 * something this machine gets to keep. It lives in `state.looks` because that is what the
 * renderer and the hit test read, and `restoreLooks` puts the local pair back when the match
 * is over — otherwise the next hotseat game starts with a stranger's hat on player two.
 */
export function adoptRivalLook(raw) {
  const look = readLook(raw);
  if (!look) return false;

  state.looks[state.seat ? 0 : 1] = look;
  return true;
}

/** Back to the pair that is actually saved, after an online match is done with. */
export function restoreLooks() {
  state.looks = looks.map(l => ({ ...l, worn: { ...l.worn } }));
}

/**
 * A character from somewhere else, checked before it is believed.
 *
 * The accessory ids are mostly the caller's problem — `wornFor` already ignores anything it
 * does not know — but they are filtered here anyway so that nothing else has to be careful,
 * and the species is an index into SPECIES and picks a body, so it is clamped.
 */
function readLook(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const species = Math.trunc(Number(raw.species));
  if (!Number.isFinite(species)) return null;

  const ids = new Set(ACCESSORIES.map(a => a.id));
  const source = raw.worn && typeof raw.worn === 'object' ? raw.worn : {};
  const worn = {};

  ACCESSORY_SLOTS.forEach(slot => {
    const id = source[slot];
    if (typeof id === 'string' && ids.has(id)) worn[slot] = id;
  });

  return { species: Math.min(Math.max(species, 0), SPECIES.length - 1), worn };
}

// --- local leaderboard ----------------------------------------------------

/**
 * Records a finished match. Deliberately local: there is no server behind this
 * game, and the menu card says so rather than pretending otherwise.
 */
export function addResult(entry) {
  results.push(entry);
  if (results.length > MAX_RESULTS) results = results.slice(-MAX_RESULTS);
  save();
}

/** Best first, then most recent. `wins` is the winner's round count. */
export function topResults(limit = 10) {
  return [...results]
    .sort((a, b) => b.wins - a.wins || b.at - a.at)
    .slice(0, limit);
}

export function clearResults() {
  results = [];
  save();
}

// --- chosen mode ----------------------------------------------------------

export function savedMode() {
  return mode;
}

export function setMode(id) {
  mode = id;
  save();
}

// --- the name that goes on the shared board --------------------------------

export function savedName() {
  return name;
}

/** Empty clears it, which is also how a player opts out of posting. */
export function setSavedName(value) {
  // eslint-disable-next-line no-control-regex
  name = String(value || '').replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, 16);
  save();
}

/**
 * Whether a play belongs to somebody sitting at this machine.
 *
 * There is no "my side" in a hot-seat duel — both sides are local — so the question is asked
 * of the modes rather than of a seat. Everywhere else the house side is 0, or the seat this end
 * took when the match came over a wire.
 */
function ours(side) {
  if (!state.cpuOn && !state.online) return true;
  return side === (state.online ? state.seat : 0);
}

/**
 * Events that belong to whoever is having the turn: the thrower, and their own shot resolving.
 * The ones not listed are the sky's, which belongs to nobody — looking up at a meteorite is
 * worth an award whoever threw the last banana.
 */
const TURN_EVENTS = new Set(['throw', 'hit', 'miss', 'selfHit', 'moonHit', 'guard', 'special', 'boomerang']);

/**
 * The one way in. Call it when something worth counting happens; it updates the
 * counters, then re-checks every achievement and announces whatever is new.
 */
export function record(event, data = {}) {
  // CLASSIC counts towards nothing. Without this, a run in a mode whose whole
  // point is to be the original would quietly fill the achievement list.
  if (!mods().achievements) return;

  // And neither does the front end. The menus are an attract mode with the same sky
  // crossing them, so a page left open on the title screen fills the list on its own:
  // measured on a clean profile, forcing the six sky events from the title screen
  // recorded all six and unlocked the sky-watcher award, reward and all, for a player
  // who had not thrown a banana. The counters are about a match, so they only move in
  // one — and the guard belongs here rather than at each call site, because the sky
  // does not know whether anybody is playing and has no business knowing.
  if (state.screen !== 'game') return;

  // Y sólo cuenta lo que pasa en casa. Sin esto la máquina te desbloquea los logros jugando
  // sola: medido en una partida contra ella, sus propias rondas subían ROUNDS-10 y sus
  // defensas THE WALL sin que el jugador tocara el teclado. `side` viene puesto de fuera
  // cuando el evento no es del que tiene el turno (la ronda que gana el otro, la caja que
  // abre el otro); si es del que juega lo pone el turno, y si es del cielo no hay lado.
  const side = data.side !== undefined ? data.side : (TURN_EVENTS.has(event) ? state.turn : undefined);
  if (side !== undefined && !ours(side)) return;

  switch (event) {
    case 'throw':
      stats.throws += 1;
      break;
    case 'hit':
      stats.hits += 1;
      stats.hitStreak = (stats.hitStreak || 0) + 1;
      stats.bestHitStreak = Math.max(stats.bestHitStreak, stats.hitStreak);
      break;
    case 'miss':
      stats.hitStreak = 0;
      break;
    case 'selfHit':
      stats.selfHits += 1;
      stats.hitStreak = 0;
      break;
    case 'moonHit':
      stats.moonHits += 1;
      break;
    case 'crate':
      stats.crates += 1;
      break;
    case 'boomerang':
      // El bumerán que ha vuelto a la mano. Es el único registro que cuenta una recuperación en
      // vez de un impacto, y por eso tiene el suyo.
      stats.boomerangs += 1;
      break;
    case 'guard':
      stats.guards += 1;
      break;
    case 'special':
      // Seen rather than counted: what this feeds is "six different ones", and a list is the
      // only shape that can answer that.
      if (!stats.specialsSeen.includes(data.id)) stats.specialsSeen.push(data.id);
      break;
    case 'roundWon':
      stats.roundsWon += 1;
      break;
    case 'matchWon':
      stats.matchesWon += 1;
      if (data.vsCpu) stats.cpuMatchesWon += 1;
      if (data.clean) stats.flawlessMatches += 1;
      if (data.bare) stats.bareMatches += 1;
      // The two ways of playing against a person, counted apart: a room is somebody who turned
      // up, and a code is somebody who was sent for.
      if (data.room) stats.roomWins += 1;
      if (data.code) stats.codeWins += 1;
      break;
    case 'sky':
      if (!stats.skySeen.includes(data.id)) stats.skySeen.push(data.id);
      break;
    default:
      return;
  }

  checkAchievements();
}

function checkAchievements() {
  let changed = false;

  ACHIEVEMENTS.forEach(def => {
    if (unlocked.includes(def.id)) return;
    if (!def.when(stats)) return;

    unlocked.push(def.id);
    changed = true;
    const reward = REWARDS[def.id];
    // El aviso va CON el nombre, y traducido.
    //
    // `def` es la fila de la tabla —lo que decide *cuándo* se gana—, y ahí no hay nombre porque el
    // nombre vive en los seis idiomas. Quien escucha es el cartel de la esquina, que tiene que decir
    // cuál, y sin esto decía "🏆 ¡LOGRO!" y se callaba el nombre: medido, el toast salía exactamente
    // así, "🏆 ACHIEVEMENT!" a secas. Es el mismo `t()` que usa `achievementList()`, para que la
    // lista del equipamiento y el cartel no puedan decir dos cosas distintas del mismo logro.
    listeners.forEach(fn => fn({ ...def, name: t(`ach.${def.id}.name`) }, reward));
  });

  if (changed) save();
}
